import { gatewayFetch, gatewayRoot } from '../data/http';

/* УДАЛЕНИЕ АККАУНТА — СИЛАМИ KEYCLOAK. Кабинет только начинает и подводит итог;
   удаляет Keycloak, и гейтвей на пути браузера не стоит.

     1. GET {gw}/api/v1/account/delete/link?redirect_url={origin}/account-deleted
        → { link } — страница Keycloak с действием «удалить аккаунт», собранная
        гейтвеем для ВОШЕДШЕГО пациента;
     2. уход на этот адрес. Keycloak заново спрашивает код из СМС, показывает
        своё «удалить аккаунт?» и удаляет пользователя;
     3. Keycloak возвращает на {origin}/account-deleted?ticket=…&kc_action_status=… ;
     4. POST {gw}/api/v1/account/delete/complete { ticket, kc_action_status } —
        гейтвей сверяется с Keycloak, что аккаунта правда нет, и отмечает это у себя.

   Шаг 4 идёт БЕЗ СЕССИИ, и это норма: пользователя в Keycloak уже нет, его куки
   мертвы. Кто удалялся, гейтвей узнаёт из подписанного `ticket`, а не из куки.

   ⚠️ АДРЕС ВОЗВРАТА ДОЛЖЕН БЫТЬ РАЗРЕШЁН У КЛИЕНТА `web_ui` В KEYCLOAK — со
   звёздочкой на конце (`…/account-deleted*`): он возвращается с `?ticket=…`.
   Неразрешённый адрес даёт отказ Keycloak до отрисовки страницы, как и при входе. */

/** Куда Keycloak вернёт пациента. Из ТЕКУЩЕГО происхождения, как и адрес входа:
    стендов несколько, и у каждого он свой. */
export function deletionRedirectUri(): string {
    return `${window.location.origin}/account-deleted`;
}

/** Шаги 1-2. Возвращает управление ТОЛЬКО при отказе: успех — это уход со страницы. */
export async function startDeletion(): Promise<{ message: string }> {
    if (gatewayRoot() === null) return { message: 'удаление недоступно в этом окружении' };
    try {
        const res = await gatewayFetch(
            `/account/delete/link?redirect_url=${encodeURIComponent(deletionRedirectUri())}`,
        );
        if (res.status === 401) return { message: 'войдите заново и повторите' };
        if (!res.ok) return { message: 'не удалось начать удаление' };
        const body = await res.json();
        const link = typeof body?.link === 'string' ? body.link : '';
        if (!link) return { message: 'гейтвей не дал адрес удаления' };
        window.location.href = link;
        return { message: '' }; // уходим со страницы
    } catch {
        return { message: 'нет связи, попробуйте ещё раз' };
    }
}

/** Что принёс Keycloak на `/account-deleted`. */
export function deletionFromLocation(): { ticket: string | null; status: string | null } {
    const q = new URLSearchParams(window.location.search);
    return { ticket: q.get('ticket'), status: q.get('kc_action_status') };
}

export type DeletionResult =
    /** Keycloak подтвердил: аккаунта нет. */
    | { kind: 'deleted' }
    /** Пациент передумал на странице Keycloak — ничего не удалено. */
    | { kind: 'cancelled' }
    /** Ничего не удалено: Keycloak отказал или аккаунт на месте. */
    | { kind: 'failed'; message: string }
    /** Удалено ли — неизвестно: гейтвей не дозвался Keycloak. Повтор безопасен. */
    | { kind: 'retry'; message: string };

/** Шаг 4: переслать гейтвею то, что принёс Keycloak. Можно повторять: повтор
    находит того же удалённого пользователя и ничего не меняет. */
export async function finishDeletion(ticket: string, status: string | null): Promise<DeletionResult> {
    try {
        const res = await gatewayFetch('/account/delete/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ticket, kc_action_status: status }),
        });
        if (res.ok) {
            const body = await res.json();
            if (body?.status === 'deleted') return { kind: 'deleted' };
            if (body?.status === 'cancelled') return { kind: 'cancelled' };
            return { kind: 'failed', message: 'Keycloak не удалил аккаунт' };
        }
        if (res.status === 409) return { kind: 'failed', message: 'аккаунт не удалён' };
        if (res.status === 400) return { kind: 'failed', message: 'ссылка на удаление устарела — начните заново' };
        return { kind: 'retry', message: 'не удалось подтвердить удаление' };
    } catch {
        return { kind: 'retry', message: 'нет связи, попробуйте ещё раз' };
    }
}

/* СМЕНА ПОЧТЫ — НЕ УХОДЯ ИЗ КАБИНЕТА. Код приходит НА НОВЫЙ адрес, пациент
   вводит его здесь, и только после этого гейтвей пишет адрес в Keycloak — уже
   подтверждённым. Через `write/personal` почту не принимают (403): по почте
   входят (magic link, код на почту), и адрес, который никто не подтвердил, был
   бы входом для владельца чужого ящика — хватило бы опечатки.

     1. POST {gw}/api/v1/account/email/code    { email } → { expires_in }
     2. POST {gw}/api/v1/account/email/confirm { code }  → { email }

   Новый запрос кода заменяет прежний: работает только код из последнего письма. */

export type EmailCodeResult = { ok: true; expiresIn: number } | { ok: false; message: string };

/** Шаг 1. Keycloak пока не трогается — почта сменится только на шаге 2. */
export async function requestEmailCode(email: string): Promise<EmailCodeResult> {
    if (gatewayRoot() === null) return { ok: false, message: 'смена почты недоступна в этом окружении' };
    try {
        const res = await gatewayFetch('/account/email/code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.trim() }),
        });
        if (res.ok) {
            const body = await res.json();
            return { ok: true, expiresIn: typeof body?.expires_in === 'number' ? body.expires_in : 0 };
        }
        const reason = await errorMessage(res);
        if (res.status === 400) return { ok: false, message: 'проверьте адрес почты' };
        if (res.status === 401) return { ok: false, message: 'войдите заново и повторите' };
        if (res.status === 409) return { ok: false, message: 'это уже ваша почта' };
        if (res.status === 429) {
            return {
                ok: false,
                message: /just sent/i.test(reason)
                    ? 'код уже отправлен — новый можно запросить через минуту'
                    : 'слишком много запросов кода, попробуйте завтра',
            };
        }
        if (res.status === 503) return { ok: false, message: 'смена почты пока недоступна' };
        return { ok: false, message: 'не удалось отправить код, попробуйте ещё раз' };
    } catch {
        return { ok: false, message: 'нет связи, попробуйте ещё раз' };
    }
}

export type EmailConfirmResult =
    | { ok: true; email: string }
    /** `restart` — код больше не сработает: истёк, сгорел от попыток или адрес
        занят. Нужен новый адрес или новый код, а не повтор ввода. */
    | { ok: false; message: string; restart: boolean };

/** Шаг 2. Совпал — адрес уже записан в аккаунт, подтверждённым. */
export async function confirmEmailCode(code: string): Promise<EmailConfirmResult> {
    try {
        const res = await gatewayFetch('/account/email/confirm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: code.trim() }),
        });
        if (res.ok) {
            const body = await res.json();
            return { ok: true, email: typeof body?.email === 'string' ? body.email : '' };
        }
        if (res.status === 400) {
            // «Wrong code, N attempts left» — число берём из ответа, слова свои.
            const left = /(\d+) attempts? left/i.exec(await errorMessage(res));
            return {
                ok: false,
                message: left ? `неверный код, осталось попыток: ${left[1]}` : 'неверный код',
                restart: false,
            };
        }
        if (res.status === 410) return { ok: false, message: 'код больше не действует — запросите новый', restart: true };
        if (res.status === 409) return { ok: false, message: 'эта почта уже привязана к другому аккаунту', restart: true };
        if (res.status === 401) return { ok: false, message: 'войдите заново и повторите', restart: false };
        // 502: Keycloak не ответил, код остаётся в силе — повтор того же кода безопасен.
        return { ok: false, message: 'не удалось сохранить почту, попробуйте ещё раз', restart: false };
    } catch {
        return { ok: false, message: 'нет связи, попробуйте ещё раз', restart: false };
    }
}

async function errorMessage(res: Response): Promise<string> {
    try {
        const body = await res.json();
        return typeof body?.errorMessage === 'string' ? body.errorMessage : '';
    } catch {
        return '';
    }
}
